import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash2, Filter, Pencil } from "lucide-react";
import { toast } from "sonner";
import { CompanyCombobox } from "@/components/EntityComboboxes";
import { QuickAddCompanyDialog } from "@/components/QuickAddDialogs";

const EMPTY = { name: "", email: "", phone: "", mobile: "", title: "", company_id: "", notes: "" };

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [quickCompanyOpen, setQuickCompanyOpen] = useState(false);
  const qc = useQueryClient();

  const load = useCallback(async () => {
    const [c, co] = await Promise.all([
      api.get("/contacts", { params: q ? { q } : {} }),
      api.get("/companies"),
    ]);
    setContacts(c.data);
    setCompanies(co.data);
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const companyMap = useMemo(() => Object.fromEntries(companies.map((c) => [c.id, c.name])), [companies]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      name: c.name || "", email: c.email || "", phone: c.phone || "", mobile: c.mobile || "",
      title: c.title || "", company_id: c.company_id || "", notes: c.notes || "",
    });
    setOpen(true);
  };

  const formIsValid = !!form.name.trim() && !!form.company_id;

  const save = async () => {
    if (!formIsValid) return;
    setSaving(true);
    try {
      const payload = { ...form, email: form.email || null };
      if (editingId) {
        await api.patch(`/contacts/${editingId}`, payload);
        toast.success("Contact updated");
      } else {
        await api.post("/contacts", payload);
        toast.success("Contact created");
      }
      setOpen(false); setForm(EMPTY); setEditingId(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this contact?")) return;
    try {
      await api.delete(`/contacts/${id}`);
      toast.success("Contact removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="contacts-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-2">People</div>
          <h1 className="text-3xl tracking-tight font-medium">Contacts</h1>
          <p className="text-sm text-muted-foreground mt-1">{contacts.length} total</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
            <Input
              data-testid="contacts-search"
              placeholder="Filter contacts — name, email, title…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 w-72"
            />
          </div>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm(EMPTY); } }}>
            <DialogTrigger asChild>
              <Button data-testid="new-contact-btn" onClick={openCreate} className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <Plus className="h-4 w-4 mr-1.5" /> New contact
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingId ? "Edit contact" : "New contact"}</DialogTitle>
                <DialogDescription className="sr-only">
                  Contacts must belong to a company. Fields marked * are required.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Name <span className="text-destructive">*</span></Label>
                  <Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Company <span className="text-destructive">*</span></Label>
                  <div className="flex gap-1.5">
                    <CompanyCombobox
                      companies={companies}
                      value={form.company_id}
                      onChange={(id) => setForm({ ...form, company_id: id })}
                      testId="contact-company"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 shrink-0"
                      onClick={() => setQuickCompanyOpen(true)}
                      title="Add a new company"
                      data-testid="contact-quick-add-company"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Email</Label>
                  <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Phone</Label>
                    <Input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Office / landline" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Mobile</Label>
                    <Input data-testid="contact-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="+91 …" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Title</Label>
                  <Input data-testid="contact-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} data-testid="contact-cancel">Cancel</Button>
                <Button onClick={save} disabled={saving || !formIsValid} data-testid="contact-save" className="bg-primary text-primary-foreground hover:bg-primary/90">
                  {saving ? "Saving…" : editingId ? "Save" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <QuickAddCompanyDialog
        open={quickCompanyOpen}
        onOpenChange={setQuickCompanyOpen}
        onCreated={(newCompany) => {
          setCompanies((prev) => [...prev, newCompany]);
          setForm((f) => ({ ...f, company_id: newCompany.id }));
          qc.invalidateQueries({ queryKey: ["companies"] });
        }}
      />

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-secondary/60 border-b border-border">
            <tr className="text-left">
              <th className="px-4 py-2.5 font-medium text-muted-foreground">Name</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground">Title</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground">Company</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground">Email</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground">Phone</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground">Mobile</th>
              <th className="px-4 py-2.5 w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {contacts.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No contacts yet.</td></tr>
            )}
            {contacts.map((c) => (
              <tr key={c.id} data-testid={`contact-row-${c.id}`} className="hover:bg-accent/40 transition-colors duration-150">
                <td className="px-4 py-3 font-medium">{c.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{c.title || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{companyMap[c.company_id] || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{c.email || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{c.phone || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{c.mobile || "—"}</td>
                <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                  <button
                    data-testid={`edit-contact-${c.id}`}
                    onClick={() => openEdit(c)}
                    className="text-muted-foreground hover:text-primary transition-colors duration-150"
                    aria-label="Edit contact"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={`delete-contact-${c.id}`}
                    onClick={() => remove(c.id)}
                    className="text-muted-foreground hover:text-destructive transition-colors duration-150"
                    aria-label="Delete contact"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
